/**
 * Authentication state store using Zustand.
 */
import { create } from 'zustand';
import { authApi, UserResponse, UserStatsResponse, TierInfoResponse } from '../api/auth';

interface AuthState {
  user: UserResponse | null;
  stats: UserStatsResponse | null;
  tierInfo: TierInfoResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  loadStats: () => Promise<void>;
  loadTierInfo: () => Promise<void>;
  setUser: (user: UserResponse) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  stats: null,
  tierInfo: null,
  isAuthenticated: false,
  isLoading: true,

  login: async (email: string, password: string) => {
    const tokens = await authApi.login({ email, password });

    // Store tokens
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);

    // Load user data
    const user = await authApi.getCurrentUser();

    set({ user, isAuthenticated: true });
  },

  logout: async () => {
    try {
      await authApi.logout();
    } finally {
      // Clear tokens and state
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');

      set({ user: null, isAuthenticated: false });
    }
  },

  loadUser: async () => {
    const token = localStorage.getItem('access_token');

    if (!token) {
      set({ isLoading: false });
      return;
    }

    try {
      const user = await authApi.getCurrentUser();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (error) {
      // Token invalid
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  setUser: (user: UserResponse) => {
    set({ user, isAuthenticated: true });
  },

  loadStats: async () => {
    try {
      const stats = await authApi.getUserStats();
      set({ stats });
    } catch (error) {
      console.error('Failed to load user stats:', error);
    }
  },

  loadTierInfo: async () => {
    try {
      const tierInfo = await authApi.getTierInfo();
      set({ tierInfo });
    } catch (error) {
      console.error('Failed to load tier info:', error);
    }
  },
}));
